import { useState } from 'react'
import { useRunBookingIntervention, type BookingIntervention } from '@/entities/booking'
import { cn } from '@/shared/lib/cn'
import { SectionCard } from '@/shared/ui'
import { ManagerReasonPrompt } from '@/features/manager-reason'
import s from './BookingInterventionCard.module.css'

/** Идентификатор абзаца с причиной — по нему кнопка ссылается на своё объяснение. */
const noteId = (key: string) => `intervention-note-${key}`

type BookingInterventionCardProps = {
  bookingId: string
  interventions: ReadonlyArray<BookingIntervention>
}

/**
 * «Вмешаться в эту бронь» (prototype/Prototype.dc.html:2338) — правки на живой брони: пропустить
 * шаг, взять её на себя, изменить сумму, перевести на новую версию сценария, отменить.
 *
 * ⚠️ До них не вело ни одного пути. Четыре правки **существуют в сервисах и просто не были
 * проброшены в интерфейс** (3.2B) — это не «работает плохо», а «ещё не подключено»; у отмены брони
 * механизм внутри тоже есть, но ручки для менеджера не было вовсе.
 *
 * Два правила, которые здесь важнее оформления:
 *
 * 1. **Недоступное показываем погашенным с причиной, а не прячем.** Спрятанной кнопки менеджер не
 *    найдёт и решит, что функции нет; погашенная отвечает на вопрос «почему сейчас нельзя».
 *    Доступность считает сервер: клиент не знает ни где стоит бронь, ни есть ли ручные правки.
 * 2. **Отказ — это ответ, а не сбой.** Перевод на новую версию система вправе отклонить и
 *    объяснить причины списком (бронь в объединении веток, уже есть ручные правки). Показываем их
 *    как причины рядом с операцией: красный тост «не удалось» сказал бы менеджеру, что он что-то
 *    сломал, — а он ничего не ломал.
 */
export function BookingInterventionCard({
  bookingId,
  interventions,
}: BookingInterventionCardProps) {
  const run = useRunBookingIntervention(bookingId)
  const [pending, setPending] = useState<BookingIntervention | null>(null)
  // Причины отказа живут до следующей попытки: это ответ на конкретное нажатие, и убирать его
  // раньше, чем менеджер что-то сделает, значило бы спрятать объяснение.
  const [refused, setRefused] = useState<{ label: string; reasons: string[] } | null>(null)

  const blocked = interventions.filter(
    (intervention) => !intervention.available && intervention.unavailableReason,
  )
  // Недоступны ВСЕ и причина одна (бронь отменена, бронь завершилась) — говорим её ОДИН раз,
  // строкой сверху вместо общей присказки. Пять одинаковых абзацев под кнопками — шум, в котором
  // тонет случай, когда причины разные; а спрятать их вовсе значило бы оставить погашенные кнопки
  // без объяснения. Причину при этом называет СЕРВЕР — своей догадки про статус здесь нет.
  const reasons = new Set(blocked.map((intervention) => intervention.unavailableReason))
  const sharedReason =
    blocked.length === interventions.length && reasons.size === 1 ? [...reasons][0] : undefined

  return (
    <SectionCard className={s.card} eyebrow="Вмешаться в эту бронь">
      <div className={s.body}>
        <p className={s.lead}>
          {sharedReason ??
            'Правки касаются только этой брони — сценарий и остальные брони не меняются. Каждое действие попадёт в историю.'}
        </p>

        <div className={s.buttons}>
          {interventions.map((intervention) => (
            <button
              key={intervention.key}
              type="button"
              className={cn(s.button, intervention.nature === 'reject' ? s.reject : undefined)}
              // `aria-disabled`, а не `disabled`: настоящий disabled убирает кнопку из порядка
              // обхода, и клавиатурой до объяснения «почему нельзя» уже не добраться.
              aria-disabled={intervention.available ? undefined : true}
              data-blocked={intervention.available ? undefined : 'true'}
              // Причина висит отдельным абзацем — связываем её с кнопкой явно, иначе скринридер
              // прочитает «Пропустить шаг, недоступно» и не скажет почему.
              aria-describedby={
                !intervention.available && !sharedReason && intervention.unavailableReason
                  ? noteId(intervention.key)
                  : undefined
              }
              // Пока летит любая правка, гасим все: каждая возвращает бронь целиком, и второй
              // ответ затёр бы первый — гонка, исход которой зависит от порядка приземления.
              disabled={run.isPending}
              title={intervention.description}
              onClick={() => {
                if (!intervention.available) return
                setRefused(null)
                setPending(intervention)
              }}
            >
              {intervention.label}
            </button>
          ))}
        </div>

        {/* Причина недоступности — видимым текстом под кнопками, а не только в `title`: тултип не
            достаётся ни с клавиатуры, ни с тача, а погашенная кнопка без объяснения — тупик. */}
        {sharedReason
          ? null
          : blocked.map((intervention) => (
              <p key={intervention.key} id={noteId(intervention.key)} className={s.note}>
                <span className={s.noteName}>{intervention.label}</span> —{' '}
                {intervention.unavailableReason}
              </p>
            ))}

        {/* Живая область смонтирована ВСЕГДА, а не появляется вместе с текстом: регион, которого
            до изменения не было, многие скринридеры не зачитывают — и отказ прошёл бы мимо, ведь
            фокус после диалога возвращается на ту же кнопку и на вид ничего не меняется. */}
        <div aria-live="polite">
          {refused ? (
            <div className={s.refused}>
              <p className={s.refusedTitle}>«{refused.label}» — так сделать не получилось</p>
              {refused.reasons.length > 0 ? (
                <ul className={s.refusedList}>
                  {refused.reasons.map((reason, index) => (
                    <li key={`${index}:${reason}`}>{reason}</li>
                  ))}
                </ul>
              ) : (
                // Контракт требует непустой список причин. Пришёл пустой — молчать нельзя: у
                // менеджера на экране осталось бы «не получилось» без единого слова почему.
                <p className={s.refusedList}>Сервер не назвал причину — попробуйте позже.</p>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {pending ? (
        <ManagerReasonPrompt
          key={pending.key}
          spec={{
            title: pending.label,
            confirmLabel: pending.label,
            description: pending.description,
            isReject: pending.nature === 'reject',
            needsAmount: pending.needsAmount,
            // Подпись выбора — тоже с сервера: он один знает, ЧТО выбирают. Своя строка здесь
            // означала бы, что фронт знает набор операций наперёд.
            ...(pending.options && pending.options.length > 0
              ? {
                  options: {
                    label: pending.optionsLabel ?? 'Выберите вариант',
                    items: pending.options,
                  },
                }
              : {}),
          }}
          isPending={run.isPending}
          onCancel={() => setPending(null)}
          onConfirm={async (payload) => {
            try {
              const result = await run.mutateAsync({
                key: pending.key,
                body: {
                  reason: payload.reason,
                  ...(payload.amount === undefined ? {} : { amount: payload.amount }),
                  ...(payload.option === undefined ? {} : { option: payload.option }),
                },
              })
              // Отказ приезжает УСПЕШНЫМ ответом — значит закрываем диалог и показываем причины
              // на карточке. Оставить диалог открытым значило бы предложить нажать ещё раз то,
              // что уже объяснено, почему не сработает.
              if (!result.applied) {
                setRefused({ label: pending.label, reasons: result.refusedReasons ?? [] })
              }
              setPending(null)
            } catch {
              // Ошибку ручки показывает тост на MutationCache; ловим, чтобы не было unhandled
              // rejection. Отказ по существу сюда не попадает — он приходит с кодом 200.
            }
          }}
        />
      ) : null}
    </SectionCard>
  )
}
